import { User } from '@/types/User';
import { getImageURL } from '@/utils/ERPUtils';
import { clearCsrfToken, setCsrfAuthenticated } from '@/utils/csrf';
import axios from 'axios';


export interface LoginResponse {
  message: string;
  home_page?: string;
  user?: User;
  [key: string]: any;
}

export interface AuthError {
  message: string;
  exc_type?: string;
  [key: string]: any;
}

class AuthService {
  private baseUrl: string;
  private loginEndpoint: string;
  private logoutEndpoint: string;
  private sessionUserEndpoint: string;
  
  constructor() {
    this.baseUrl = (typeof process !== 'undefined' && process.env.REACT_APP_API_URL) || '';
    this.loginEndpoint = '/api/method/login';
    this.logoutEndpoint = '/api/method/logout';
    this.sessionUserEndpoint = '/api/method/orgain.user_api.get_user_details';
    
    // Always send cookies
    axios.defaults.withCredentials = true;
  }

  private getFullUrl(endpoint: string): string {
    return `${this.baseUrl}${endpoint}`;
  }

  private handleAuthError(error: any): AuthError {
    if (error.response) {
      const serverError = error.response.data;
      return {
        message: serverError.message || serverError.exc || 'Authentication failed',
        exc_type: serverError.exc_type,
        status: error.response.status
      };
    } else if (error.request) {
      return {
        message: 'Network error. Please check your connection and try again.',
        type: 'network_error'
      };
    } else {
      return {
        message: error.message || 'An unexpected error occurred',
        type: 'unknown_error'
      };
    }
  }

  async signIn(email: string, password: string): Promise<{ user?: User; error?: AuthError }> {
    try {
      const response: AxiosResponse<LoginResponse> = await axios.post(
        this.getFullUrl(this.loginEndpoint),
        new URLSearchParams({ usr: email, pwd: password }),
        
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          timeout: 10000
        }
      );

      if (!!response.data.full_name) {
        // Login rotates the session id + CSRF token server-side. Drop the
        // cached token so the next mutating request fetches the NEW session's
        // token instead of failing with CSRFTokenError until a manual refresh.
        clearCsrfToken();
        // A real session now exists - allow the CSRF helper to fetch the
        // (rotated) token. getCurrentUser() re-confirms this below.
        setCsrfAuthenticated(true);
        // After login, cookies are set. Get full user data from frappe.
        return await this.getCurrentUser();
      } else {
        return { error: { message: response.data.message || 'Login failed', type: 'login_failed' } };
      }
    } catch (error: any) {
      return { error: this.handleAuthError(error) };
    }
  }

  async getCurrentUser(): Promise<{ user?: User; error?: AuthError }> {
    try {
      // orgain.user_api.get_user_details is allow_guest=True, so it returns
      // 200 with a status field rather than a 401. Treat an explicit error
      // status (or missing data) as "no session" - the old check compared
      // an object against the string 'No session' which was always truthy
      // and caused the client to proceed with `profileData = undefined`,
      // throwing and bouncing the user to /login on page reload.
      // 30s timeout - session check needs to tolerate gunicorn cold-starts
      // and slow Redis/DB lookups. 5s was aborting legitimate requests on
      // page reload and the abort showed up as "cancelled" in DevTools,
      // which the catch treated as no-session and bounced to /login.
      const response = await axios.get(this.getFullUrl(this.sessionUserEndpoint), { timeout: 30000 });
      const profileData = response.data?.message?.data;

      if (response.data?.message?.status !== 'success' || !profileData) {
        // No session - keep the CSRF helper from calling the (non-guest)
        // token endpoint and triggering a PermissionError.
        setCsrfAuthenticated(false);
        return { error: { message: 'No active session', type: 'no_session' } };
      }
      // Confirmed authenticated session - the CSRF helper may now fetch the token.
      setCsrfAuthenticated(true);

      const userData: User = {
        full_name: "",
        id: profileData.email,
        name: profileData.full_name || profileData.email?.split('@')[0] || 'Unknown',
        email: profileData.email || '',
        status: profileData.enabled ? 'active' : 'inactive',
        department: profileData.department || "",
        first_name: profileData?.first_name || "",
        last_name: profileData?.last_name || "",
        username: profileData?.username || "",
        middle_name: profileData?.middle_name || "",
        gender: profileData?.gender || "",
        birthdate: profileData?.birth_date || "",
        interests: profileData?.interest || "",
        roles: profileData?.roles?.map(e => e?.name) || [],
        lastActive: profileData.last_login,
        avatar: getImageURL(profileData?.user_image, profileData?.modified) || "",
        employee_id: profileData.email,
        phone: profileData?.phone || "",
        mobile_number: profileData?.mobile_no || "",
        location: profileData?.location || "",
        bio: profileData?.bio || "",
        timezone: profileData?.time_zone || "",
        job_role: profileData?.job_role || "",
        notes: "",
        email_confirmed: true,
        last_login: profileData.last_login,
        is_pending: profileData?.pending_user || false,
      };
      return { user: userData };
    } catch (error: any) {
      // Session check failed (no/expired session, network) - treat as Guest so
      // the CSRF helper won't hit the non-guest token endpoint.
      setCsrfAuthenticated(false);
      return { error: this.handleAuthError(error) };
    }
  }

  async signOut(): Promise<{ error?: AuthError }> {
    try {
      await axios.post(this.getFullUrl(this.logoutEndpoint), {}, { timeout: 5000 });
      // Invalidate the cached CSRF token so the next login picks up a
      // fresh session token instead of reusing the old (now-invalid) one.
      clearCsrfToken();
      setCsrfAuthenticated(false);
      return {};
    } catch (error: any) {
      // Still clear it on error - if the server-side logout partially
      // succeeded, we must not keep the stale token around.
      clearCsrfToken();
      setCsrfAuthenticated(false);
      return { error: this.handleAuthError(error) };
    }
  }

  async checkSession(): Promise<{ user?: User; error?: AuthError }> {
    return this.getCurrentUser(); // always rely on frappe cookies
  }
}

export const authService = new AuthService();
